
import { create } from 'zustand';
import { api } from '@/lib/api';
import type { NotificationListData, NotificationResponse, NotificationPreferencesData, NotificationPreferencesResponse } from '@/lib/shared-types/api/endpoints/notifications';
import type {
  NotificationsStore,
  Notification,
  NotificationFilters,
  StoreNotificationPreferences,
  NotificationsResponse,
  NotificationType
} from './types';


// API functions
const apiFetchNotifications = async (
  filters?: Partial<NotificationFilters>,
  page = 1,
  pageSize = 20
): Promise<NotificationsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (filters?.read !== undefined) params.set('read', filters.read.toString());
  if (filters?.type) params.set('type', Array.isArray(filters.type) ? filters.type.join(',') : filters.type);

  const response = await api.get<NotificationListData>(`api/notifications?${params}`);

  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to fetch notifications');
  }

  const result = response.data!;

  return {
    notifications: (result.notifications || []).map((notification: NotificationResponse): Notification => ({
      ...notification,
      type: notification.type as NotificationType,
      data: notification.data
    })),
    pagination: result.pagination,
    unreadCount: result.unreadCount || 0
  };
};


const apiMarkAsRead = async (notificationId: number): Promise<void> => {
  const response = await api.put(`api/notifications/${notificationId}/read`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to mark notification as read');
  }
};

const apiMarkAsUnread = async (notificationId: number): Promise<void> => {
  const response = await api.put(`api/notifications/${notificationId}/unread`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to mark notification as unread');
  }
};

const apiMarkAllAsRead = async (): Promise<void> => {
  const response = await api.post('api/notifications/mark-read');
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to mark all notifications as read');
  }
};

const apiDeleteNotification = async (notificationId: number): Promise<void> => {
  const response = await api.delete(`api/notifications/${notificationId}`);
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to delete notification');
  }
};

const apiDeleteAllRead = async (): Promise<void> => {
  const response = await api.delete('api/notifications/read-all');
  if (!response.success) {
    throw new Error(response.error?.message || 'Failed to delete read notifications');
  }
};

const apiUpdatePreferences = async (preferences: Partial<StoreNotificationPreferences>): Promise<void> => {
  const apiPrefs = storePreferencesToApi(preferences);
  await api.put('api/notifications/preferences', apiPrefs);
};

/**
 * Convert store preferences format to API format
 */
const storePreferencesToApi = (storePrefs: Partial<StoreNotificationPreferences>): Partial<NotificationPreferencesData> => {
  const apiPrefs: Partial<NotificationPreferencesData> = {
    channels: {
      email: storePrefs.email?.enabled || false,
      push: storePrefs.push?.enabled || false,
      inApp: storePrefs.inApp?.enabled || false,
    },
    events: {
      streamLive: {
        enabled: storePrefs.email?.types?.includes('stream_live') || false,
        channels: ['email'] as const,
      },
      newComment: {
        enabled: storePrefs.email?.types?.includes('comment') || false,
        channels: ['email'] as const,
      },
      mention: {
        enabled:
          storePrefs.email?.types?.includes('mention') ||
          storePrefs.email?.types?.includes('comment_mention') ||
          false,
        channels: ['email'] as const,
      },
      like: {
        enabled: storePrefs.email?.types?.includes('like') || false,
        channels: ['email'] as const,
      },
      invite: {
        enabled: storePrefs.email?.types?.includes('invite') || false,
        channels: ['email'] as const,
      },
      system: {
        enabled: storePrefs.email?.types?.includes('system') || false,
        channels: ['email'] as const,
      },
    },
  };
  return apiPrefs;
};

/**
 * Convert API preferences format to store format
 */
const apiPreferencesToStore = (apiPrefs: NotificationPreferencesData): StoreNotificationPreferences => {
  const emailTypes: NotificationType[] = [];
  const pushTypes: NotificationType[] = [];
  const inAppTypes: NotificationType[] = [];

  // Map API events to store types
  if (apiPrefs.events.streamLive?.enabled) {
    emailTypes.push('stream_live');
  }
  if (apiPrefs.events.newComment?.enabled) {
    emailTypes.push('comment');
  }
  if (apiPrefs.events.mention?.enabled) {
    emailTypes.push('mention');
    emailTypes.push('comment_mention');
  }
  if (apiPrefs.events.like?.enabled) {
    emailTypes.push('like');
  }
  if (apiPrefs.events.invite?.enabled) {
    emailTypes.push('invite');
  }
  if (apiPrefs.events.system?.enabled) {
    emailTypes.push('system');
  }

  return {
    email: {
      enabled: apiPrefs.channels.email,
      types: emailTypes,
      frequency: 'immediate'
    },
    push: {
      enabled: apiPrefs.channels.push,
      types: pushTypes
    },
    inApp: {
      enabled: apiPrefs.channels.inApp,
      types: inAppTypes,
      soundEnabled: true
    }
  };
};


const defaultPreferences: StoreNotificationPreferences = {
  email: {
    enabled: true,
    types: ['stream_live', 'access_granted', 'system_announcement'],
    frequency: 'immediate'
  },
  push: {
    enabled: false,
    types: ['stream_live', 'comment_reply']
  },
  inApp: {
    enabled: true,
    types: ['stream_live', 'comment_reply', 'comment_mention', 'access_granted'],
    soundEnabled: true
  }
};

export const useNotificationsStore = create<NotificationsStore>((set, get) => ({
  // Initial state
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  filters: {
    read: false // Show unread by default
  },
  pagination: {
    page: 1,
    pageSize: 20,
    total: 0,
    hasMore: false
  },
  preferences: defaultPreferences,
  isConnected: false,
  lastUpdate: null,
  pushPermission: 'default',

  // Actions
  fetchNotifications: async (filters) => {
    try {
      set({ isLoading: true, error: null });

      if (filters) {
        set((state) => ({
          filters: { ...state.filters, ...filters }
        }));
      }

      const currentFilters = get().filters;
      const response = await apiFetchNotifications(currentFilters);

      set({
        notifications: response.notifications,
        unreadCount: response.unreadCount,
        pagination: response.pagination,
        isLoading: false,
        error: null
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch notifications';
      set({
        error: errorMessage,
        isLoading: false,
        notifications: []
      });
      throw error;
    }
  },

  fetchUnreadCount: async () => {
    try {
      const response = await apiFetchNotifications({ read: false }, 1, 1);
      set({ unreadCount: response.pagination?.total || 0 });
    } catch (error) {
      // Silently handle unread count failures - these are background operations
      // The global API interceptor will show toasts for network errors
      console.error('Failed to fetch unread count:', error);
    }
  },


  markAsRead: async (notificationId) => {
    try {
      await apiMarkAsRead(notificationId);

      // Optimistically update the notification
      set((state) => ({
        notifications: state.notifications.map(notification =>
          notification.id === notificationId
            ? {
              ...notification,
              read: true,
              readAt: new Date().toISOString()
            }
            : notification
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      }));

    } catch (error) {
      // Revert optimistic update on error
      set((state) => ({
        notifications: state.notifications.map(notification =>
          notification.id === notificationId
            ? {
              ...notification,
              read: false,
              readAt: undefined
            }
            : notification
        ),
        unreadCount: state.unreadCount + 1
      }));
      throw error;
    }
  },

  markAsUnread: async (notificationId) => {
    try {
      await apiMarkAsUnread(notificationId);

      set((state) => {
        const target = state.notifications.find((notification) => notification.id === notificationId);
        const wasRead = Boolean(target?.read);

        return {
          notifications: state.notifications.map(notification =>
            notification.id === notificationId
              ? {
                ...notification,
                read: false,
                readAt: undefined
              }
              : notification
          ),
          unreadCount: wasRead ? state.unreadCount + 1 : state.unreadCount
        };
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to mark notification as unread';
      set({ error: errorMessage });
      throw error;
    }
  },



  markAllAsRead: async () => {
    try {
      set({ isLoading: true, error: null });

      await apiMarkAllAsRead();

      // Update all notifications to read
      set((state) => ({
        notifications: state.notifications.map(notification => ({
          ...notification,
          read: true,
          readAt: notification.readAt || new Date().toISOString()
        })),
        unreadCount: 0,
        isLoading: false
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to mark all as read';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  deleteNotification: async (notificationId) => {
    try {
      await apiDeleteNotification(notificationId);

      // Remove the notification from the list
      set((state) => {
        const notification = state.notifications.find(n => n.id === notificationId);
        const wasUnread = notification && !notification.read;

        return {
          notifications: state.notifications.filter(n => n.id !== notificationId),
          unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount
        };
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete notification';
      set({ error: errorMessage });
      throw error;
    }
  },

  deleteAllRead: async () => {
    try {
      set({ isLoading: true, error: null });
      await apiDeleteAllRead();

      set((state) => ({
        notifications: state.notifications.filter(notification => !notification.read),
        isLoading: false
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete read notifications';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },



  bulkMarkAsRead: async (notificationIds) => {
    try {
      set({ isLoading: true, error: null });

      // Mark each notification as read
      await Promise.all(
        notificationIds.map(id => apiMarkAsRead(id))
      );

      // Update all notifications at once
      set((state) => {
        const updatedNotifications = state.notifications.map(notification =>
          notificationIds.includes(notification.id)
            ? {
              ...notification,
              read: true,
              readAt: notification.readAt || new Date().toISOString()
            }
            : notification
        );

        const newUnreadCount = Math.max(0,
          state.unreadCount - notificationIds.length
        );

        return {
          notifications: updatedNotifications,
          unreadCount: newUnreadCount,
          isLoading: false
        };
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to mark notifications as read';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },


  bulkDelete: async (notificationIds) => {
    try {
      set({ isLoading: true, error: null });

      // Delete each notification
      await Promise.all(
        notificationIds.map(id => apiDeleteNotification(id))
      );

      // Remove all notifications at once
      set((state) => {
        const deletedNotifications = state.notifications.filter(n =>
          notificationIds.includes(n.id)
        );
        const unreadDeleted = deletedNotifications.filter(n => !n.read).length;

        return {
          notifications: state.notifications.filter(n =>
            !notificationIds.includes(n.id)
          ),
          unreadCount: Math.max(0, state.unreadCount - unreadDeleted),
          isLoading: false
        };
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete notifications';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },


  updatePreferences: async (preferences) => {
    try {
      set({ isLoading: true, error: null });

      await apiUpdatePreferences(preferences);

      // Update preferences in state
      set((state) => ({
        preferences: { ...state.preferences, ...preferences },
        isLoading: false
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update preferences';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },


  fetchPreferences: async () => {
    try {
      const response = await api.get<NotificationPreferencesResponse>('api/notifications/preferences');

      if (response.success && response.data) {
        // Handle both direct data and nested preferences structure
        const data = response.data;
        const apiPrefs = ('preferences' in data ? data.preferences : data) as Partial<NotificationPreferencesData>;
        // Ensure all required properties are present with defaults
        const safeApiPrefs: NotificationPreferencesData = {
          userId: apiPrefs.userId || 0,
          channels: {
            email: apiPrefs.channels?.email ?? true,
            push: apiPrefs.channels?.push ?? true,
            inApp: apiPrefs.channels?.inApp ?? true,
          },
          events: {
            streamLive: apiPrefs.events?.streamLive || { enabled: true, channels: ['email', 'push', 'inApp'] },
            newComment: apiPrefs.events?.newComment || { enabled: true, channels: ['email', 'inApp'] },
            mention: apiPrefs.events?.mention || { enabled: true, channels: ['email', 'push', 'inApp'] },
            like: apiPrefs.events?.like || { enabled: false, channels: ['inApp'] },
            invite: apiPrefs.events?.invite || { enabled: true, channels: ['email', 'push', 'inApp'] },
            system: apiPrefs.events?.system || { enabled: true, channels: ['email', 'push', 'inApp'] },
          },
        };
        const storePrefs = apiPreferencesToStore(safeApiPrefs);
        set({ preferences: storePrefs });
        return;
      }

      // Fallback to defaults if API fails
      set({ preferences: defaultPreferences });

    } catch (error) {
      console.error('Failed to fetch preferences:', error);
      set({ preferences: defaultPreferences });
    }
  },


  // Push notification functionality
  requestPushPermission: async () => {
    try {
      if (!('Notification' in window)) {
        throw new Error('This browser does not support notifications');
      }

      const permission = await Notification.requestPermission();
      set({ pushPermission: permission });
      return permission;

    } catch (error) {
      console.error('Failed to request push permission:', error);
      set({ pushPermission: 'denied' });
      return 'denied';
    }
  },

  subscribeToPush: async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        throw new Error('Push notifications not supported');
      }

      const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
      if (!vapidPublicKey) {
        throw new Error('Push notifications are not configured: VAPID public key missing');
      }

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey
      });

      set({ pushSubscription: subscription.toJSON() });
      console.log('Subscribed to push notifications');

    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      throw error;
    }
  },

  unsubscribeFromPush: async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe();
        set({ pushSubscription: undefined });
        console.log('Unsubscribed from push notifications');
      }

    } catch (error) {
      console.error('Failed to unsubscribe from push notifications:', error);
      throw error;
    }
  },

  sendTestNotification: async () => {
    try {
      if (Notification.permission !== 'granted') {
        throw new Error('Notification permission not granted');
      }

      const notification = new Notification('Test Notification', {
        body: 'This is a test notification from Dominion Mandate',
        icon: '/icon-192x192.png',
        badge: '/badge-72x72.png',
        tag: 'test-notification'
      });

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

    } catch (error) {
      console.error('Failed to send test notification:', error);
      throw error;
    }
  },

  // Real-time functionality (using Supabase Realtime)
  connectToNotifications: async () => {
    try {
      console.log('Initializing Realtime notifications...');

      const { supabase } = await import('@/lib/supabase/client');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('No Supabase session — skipping Realtime subscription');
        return;
      }

      // Unsubscribe any existing channel first
      const existing = supabase.getChannels();
      for (const ch of existing) {
        if (ch.topic.startsWith('realtime:notifications-')) {
          await supabase.removeChannel(ch);
        }
      }

      const channelName = `notifications-${session.user.id}`;
      supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${session.user.id}`,
          },
          (payload) => {
            const notification = payload.new as any;
            set((state) => ({
              notifications: [
                {
                  ...notification,
                  type: notification.type as NotificationType,
                  data: notification.data,
                } as Notification,
                ...state.notifications,
              ],
              unreadCount: state.unreadCount + 1,
              lastUpdate: new Date().toISOString(),
            }));
          }
        )
        .subscribe((status) => {
          console.log(`[Realtime] notifications channel status: ${status}`);
          set({ isConnected: status === 'SUBSCRIBED' });
        });

      set({ isConnected: true });
      console.log('Realtime initialized for notifications');
    } catch (error) {
      console.error('Failed to initialize notifications:', error);
      throw error;
    }
  },

  disconnectFromNotifications: async () => {
    console.log('Disconnecting from Realtime notifications');
    try {
      const { supabase } = await import('@/lib/supabase/client');
      const channels = supabase.getChannels();
      for (const ch of channels) {
        if (ch.topic.startsWith('realtime:notifications-')) {
          await supabase.removeChannel(ch);
        }
      }
    } catch { /* ignore */ }
    set({ isConnected: false });
  },

  handleRealtimeNotification: (notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: state.unreadCount + 1,
      lastUpdate: new Date().toISOString()
    }));
  },

  // UI state management
  setFilters: (filters) => {
    set((state) => ({
      filters: { ...state.filters, ...filters },
      pagination: { ...state.pagination, page: 1 }
    }));
  },

  loadMoreNotifications: async () => {
    const state = get();
    if (state.isLoading || !state.pagination.hasMore) return;

    try {
      set({ isLoading: true });

      const nextPage = state.pagination.page + 1;
      const response = await apiFetchNotifications(state.filters, nextPage, state.pagination.pageSize);

      set((currentState) => ({
        notifications: [...currentState.notifications, ...response.notifications],
        pagination: response.pagination,
        isLoading: false
      }));

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load more notifications';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw error;
    }
  },

  resetNotifications: () => {
    set({
      notifications: [],
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        hasMore: false
      }
    });
  },

  // Utility functions
  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  setError: (error) => {
    set({ error, isLoading: false });
  },

  clearError: () => {
    set({ error: null });
  }
}));

// Selectors for common notifications state
export const useNotifications = () => useNotificationsStore((state) => state.notifications);
export const useUnreadCount = () => useNotificationsStore((state) => state.unreadCount);
export const useNotificationsLoading = () => useNotificationsStore((state) => state.isLoading);
export const useNotificationsError = () => useNotificationsStore((state) => state.error);
export const useNotificationPreferences = () => useNotificationsStore((state) => state.preferences);
export const useNotificationsFilters = () => useNotificationsStore((state) => state.filters);

// Helper hooks for notification operations
export const useHasUnreadNotifications = () => {
  const unreadCount = useUnreadCount();
  return unreadCount > 0;
};

export const useNotificationById = (id: number) => {
  const notifications = useNotifications();
  return notifications.find(n => n.id === id);
};

export const useUnreadNotifications = () => {
  const notifications = useNotifications();
  return notifications.filter(n => !n.read);
};

export const useReadNotifications = () => {
  const notifications = useNotifications();
  return notifications.filter(n => n.read);
};

// Helper hook for notification types
export const useNotificationTypes = () => {
  return [
    { value: 'stream_live', label: 'Stream Live', category: 'stream' },
    { value: 'comment_reply', label: 'Comment Reply', category: 'social' },
    { value: 'comment_mention', label: 'Mention', category: 'social' },
    { value: 'access_granted', label: 'Access Granted', category: 'system' },
    { value: 'system_announcement', label: 'Announcement', category: 'system' }
  ] as const;
};

// Helper hook for push notification status
export const usePushNotificationStatus = () => {
  const permission = useNotificationsStore((state) => state.pushPermission);
  const subscription = useNotificationsStore((state) => state.pushSubscription);

  return {
    permission,
    isSubscribed: !!subscription,
    canSubscribe: permission === 'granted' && !subscription,
    canUnsubscribe: !!subscription
  };
};

// Helper hook for real-time connection status
export const useNotificationConnection = () => {
  const isConnected = useNotificationsStore((state) => state.isConnected);
  const lastUpdate = useNotificationsStore((state) => state.lastUpdate);

  return {
    isConnected,
    lastUpdate,
    connectionStatus: isConnected ? 'connected' : 'disconnected'
  };
};
